import React, {ReactElement, useLayoutEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {simplifyPoints} from '../../../../../util/geometry';
import {IPoint, IShape} from '../../../../../incase-domain/elements/question/imageMarkup';
import {DEBATABLE_ANSWER_COLOR} from '../../../../../incase-domain/common/color';
import {JS_INTERACTABLE_ELEMENT} from '../../../../../constants';

interface ImageMarkupFreehandCanvasProps {
    width: number;
    height: number;
    onShapeChange: (shapes: IShape[]) => void; // For immediate UI update during drawing
    onShapeSave: (shapes: IShape[]) => void; // For saving on mouse/touch up
    shapes: IShape[];
    answered: boolean;
    isDebatable: boolean;
}

function ImageMarkupFreehandCanvas(props: ImageMarkupFreehandCanvasProps): ReactElement {
    const {width, height, onShapeChange, onShapeSave, shapes, answered, isDebatable} = props;

    const {t} = useTranslation();

    const canvasesRef = useRef<Map<number, HTMLCanvasElement> | null>(null);

    const [isDrawing, setIsDrawing] = useState<boolean>(false);

    const getClientX = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>): number => {
        return (e as React.MouseEvent).clientX || (e as React.TouchEvent).touches[0].clientX;
    };

    const getClientY = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>): number => {
        return (e as React.MouseEvent).clientY || (e as React.TouchEvent).touches[0].clientY;
    };

    const getCanvasesMap = (): Map<number, HTMLCanvasElement> => {
        if (!canvasesRef.current) {
            canvasesRef.current = new Map();
        }
        return canvasesRef.current;
    };

    useLayoutEffect(() => {
        const map = getCanvasesMap();
        map.forEach((canvas) => {
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
            }
        });
        if (canvasesRef.current?.size && canvasesRef.current?.size > 0 && shapes.length > 0) {
            shapes.forEach((shape: IShape, index) => {
                const canvas = map.get(index);
                const ctx = canvas?.getContext('2d');
                if (canvas && ctx && shape.points.length > 0) {
                    ctx.beginPath();
                    ctx.moveTo(shape.points[0].x * canvas.width, shape.points[0].y * canvas.height);
                    shape.points.forEach((point: IPoint) => {
                        ctx.lineTo(point.x * canvas.width, point.y * canvas.height);
                    });
                    ctx.fillStyle = 'rgba(255, 0, 0, 0.2)';
                    ctx.fill();
                    // eslint-disable-next-line no-nested-ternary
                    if (!isDebatable) ctx.strokeStyle = shape.isCorrect === undefined ? 'blue' : shape.isCorrect ? 'green' : 'red';
                    else ctx.strokeStyle = shape.isCorrect === undefined ? 'blue' : DEBATABLE_ANSWER_COLOR;
                    ctx.lineWidth = 2;
                    ctx.stroke();
                }
            });
        }
    }, [shapes, shapes.length, canvasesRef.current?.size, width, isDebatable]);

    const handleStart = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>): void => {
        if (!answered) {
            setIsDrawing(true);
            const canvas = getCanvasesMap().get(shapes.length);
            const ctx = canvas?.getContext('2d');
            if (canvas && ctx) {
                const rect = canvas.getBoundingClientRect();
                const x = parseFloat(((getClientX(e) - rect.left) / canvas.width).toFixed(3));
                const y = parseFloat(((getClientY(e) - rect.top) / canvas.height).toFixed(3));
                onShapeChange([...shapes, {points: [{x, y}]}]);
            }
        }
    };

    const handleMove = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>): void => {
        if (!answered) {
            const canvas = getCanvasesMap().get(shapes.length - 1);
            const ctx = canvas?.getContext('2d');
            if (isDrawing && canvas && shapes && ctx) {
                const rect = canvas.getBoundingClientRect();
                const x = parseFloat(((getClientX(e) - rect.left) / canvas.width).toFixed(3));
                const y = parseFloat(((getClientY(e) - rect.top) / canvas.height).toFixed(3));
                const currentShape = shapes[shapes.length - 1];
                currentShape.points.push({x, y});
                onShapeChange([...shapes.slice(0, shapes.length - 1), currentShape]);
            }
        }
    };

    const handleEnd = (): void => {
        if (!answered) {
            setIsDrawing(false);
            const canvas = getCanvasesMap().get(shapes.length - 1);
            if (shapes.length > 0 && canvas) {
                const currentShape = shapes[shapes.length - 1];
                let pointsToUse = currentShape.points;

                // Close the shape if it has enough points
                if (pointsToUse.length > 2) {
                    pointsToUse = [...pointsToUse, pointsToUse[0]];
                }

                // Simplify points before saving to reduce payload size
                if (pointsToUse.length > 3) {
                    pointsToUse = simplifyPoints(pointsToUse, 0.01, false);
                    if (
                        pointsToUse.length > 2 &&
                        (pointsToUse[0].x !== pointsToUse[pointsToUse.length - 1].x ||
                            pointsToUse[0].y !== pointsToUse[pointsToUse.length - 1].y)
                    ) {
                        pointsToUse = [...pointsToUse, pointsToUse[0]];
                    }
                }

                currentShape.points = pointsToUse;

                // Don't store raster - use polygon geometry instead for intersection calculations
                const updatedShapes = [...shapes.slice(0, shapes.length - 1), currentShape];
                onShapeChange(updatedShapes);
                onShapeSave(updatedShapes);
            }
        }
    };

    const handleClear = (): void => {
        onShapeChange([]);
        onShapeSave([]);
        const map = getCanvasesMap();
        map.forEach((canvas) => {
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
            }
        });
    };

    return (
        <>
            {shapes.concat({points: []}).map((s: IShape, index: number) => (
                <canvas
                    key={`answer-canvas-${index}`}
                    className={`c-imageMarkup__canvas ${!answered ? 'c-imageMarkup__canvas--pencil' : ''} ${JS_INTERACTABLE_ELEMENT}`}
                    width={width}
                    height={height}
                    ref={(node) => {
                        const map = getCanvasesMap();
                        if (node) {
                            map.set(index, node);
                        } else {
                            map.delete(index);
                        }
                    }}
                    onMouseDown={handleStart}
                    onMouseUp={handleEnd}
                    onMouseMove={handleMove}
                    onTouchStart={handleStart}
                    onTouchEnd={handleEnd}
                    onTouchMove={handleMove}
                    style={{touchAction: 'none'}}
                />
            ))}
            {!answered && shapes.length > 0 && (
                <button type="button" onClick={handleClear}>
                    {t('common.clear')}
                </button>
            )}
        </>
    );
}

export default ImageMarkupFreehandCanvas;
